var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { useRef, useEffect, useState } from "react";
import { i as useMainConfigContext, j as initDatadog } from "./public-get.api-DzhvurPF.js";
import { i as initTimeLocale, e as enableDayJsUTCPlugin } from "./date-helper-BgT2-yhf.js";
import { R as RtwInCompatibilityReasonCode, a as isRTWOfflineClient, S as SizePassportScoreCode } from "./use-circumference-measurements-query.hook-d3jyZ33a.js";
import { t as translatePassportTerm } from "./base-provider-Br-ghcgu.js";
function useDataDogMonitoringInitializer({
  isSimplifiedSession,
  isUserIdentifierEnabled,
  client,
  countryCode,
  hostPlatform
} = {
  isSimplifiedSession: false,
  isUserIdentifierEnabled: false
}) {
  const {
    config
  } = useMainConfigContext();
  const isInitialized = useRef(false);
  const cleanupRef = useRef(null);
  useEffect(() => {
    if (!config.version || !config.environment || !hostPlatform) return;
    if (isInitialized.current) return;
    let cancelled = false;
    isInitialized.current = true;
    void (() => __async(null, null, function* () {
      try {
        const cleanup = yield initDatadog({
          appVersion: config.version,
          env: config.environment,
          isStorageDisabled: isSimplifiedSession,
          isUserIdentifierEnabled,
          client,
          countryCode,
          hostPlatform
        });
        if (cancelled) {
          cleanup();
          return;
        }
        cleanupRef.current = cleanup;
      } catch (error) {
        isInitialized.current = false;
        console.error("[Meas] Error initializing logging", error);
      }
    }))();
    return () => {
      var _a;
      cancelled = true;
      (_a = cleanupRef.current) == null ? void 0 : _a.call(cleanupRef);
      cleanupRef.current = null;
      isInitialized.current = false;
    };
  }, [client, config.environment, config.version, countryCode, hostPlatform, isSimplifiedSession, isUserIdentifierEnabled]);
}
function useTimeLocale(languageCode = "EN") {
  const [isTimeLocaleInitialized, setIsTimeLocaleInitialized] = useState(false);
  useEffect(() => {
    if (!isTimeLocaleInitialized) {
      initTimeLocale(languageCode);
      setIsTimeLocaleInitialized(true);
    }
  }, [isTimeLocaleInitialized, languageCode]);
}
function useDayJsUTCPlugin() {
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (!isInitialized) {
      enableDayJsUTCPlugin();
      setIsInitialized(true);
    }
  }, [isInitialized]);
}
const getIncompatibleNotificationTitle = (t, compatibilityReport, isUserFriendlyMessage = false) => {
  var _a;
  const isCompatible = compatibilityReport ? compatibilityReport.isCompatible : true;
  if (isCompatible || !((_a = compatibilityReport == null ? void 0 : compatibilityReport.incompatibilityReasons) == null ? void 0 : _a.length)) return "";
  const incompatibilityReason = compatibilityReport.incompatibilityReasons[0];
  let notificationTitle;
  if (incompatibilityReason.compatibilityTypeCode === RtwInCompatibilityReasonCode.SizeDoesNotMatch) {
    notificationTitle = t("shared.profileNotifications.short.sizeNotMatching");
  } else if (incompatibilityReason.compatibilityTypeCode === RtwInCompatibilityReasonCode.FitBucketDoesntMatch) {
    notificationTitle = isUserFriendlyMessage ? t("shared.profileNotifications.short.fitNotMatchingImproved") : t("shared.profileNotifications.short.fitNotMatching");
  } else if (incompatibilityReason.compatibilityTypeCode === RtwInCompatibilityReasonCode.FitUpdated) {
    notificationTitle = t("shared.profileNotifications.short.fitUpdated");
  } else {
    notificationTitle = isUserFriendlyMessage ? t("shared.profileNotifications.short.alterationsNotPossibleImproved") : t("shared.profileNotifications.short.alterationsNotPossible");
  }
  return notificationTitle;
};
const getIncompatibleNotificationText = (t, compatibilityReport, isUserFriendlyMessage = false) => {
  var _a;
  const isCompatible = compatibilityReport ? compatibilityReport.isCompatible : true;
  if (isCompatible || !((_a = compatibilityReport == null ? void 0 : compatibilityReport.incompatibilityReasons) == null ? void 0 : _a.length)) {
    return "";
  }
  const incompatibilityReason = compatibilityReport.incompatibilityReasons[0];
  let incompatibilityReasonText;
  if (incompatibilityReason.compatibilityTypeCode === RtwInCompatibilityReasonCode.AlterationOutOfRange) {
    incompatibilityReasonText = isUserFriendlyMessage ? t("shared.profileNotifications.long.alterationsNotPossibleImproved") : t("shared.profileNotifications.long.alterationsNotPossible");
  } else if (incompatibilityReason.compatibilityTypeCode === RtwInCompatibilityReasonCode.FitBucketDoesntMatch) {
    incompatibilityReasonText = isUserFriendlyMessage ? t("shared.profileNotifications.long.fitNotMatchingImproved") : incompatibilityReason.explanation;
  } else if (incompatibilityReason.compatibilityTypeCode === RtwInCompatibilityReasonCode.FitUpdated) {
    incompatibilityReasonText = t("shared.profileNotifications.long.fitUpdated");
  } else {
    incompatibilityReasonText = isUserFriendlyMessage ? t("shared.profileNotifications.long.genericImproved") : incompatibilityReason.explanation;
  }
  return incompatibilityReasonText;
};
const getDisabledProfileContentCM = (t, isProfileEnabled, compatibilityReport, isOfflineCustomer = false, useAlternativeText = false) => {
  const isCompatible = compatibilityReport ? compatibilityReport.isCompatible : true;
  if (!isCompatible) {
    return {
      title: getIncompatibleNotificationTitle(t, compatibilityReport, !isOfflineCustomer),
      text: getIncompatibleNotificationText(t, compatibilityReport, !isOfflineCustomer)
    };
  }
  if (!isProfileEnabled) {
    return {
      title: t("shared.profileNotifications.short.profileInactive"),
      text: useAlternativeText ? t("shared.profileNotifications.long.inactiveProfileClick") : t("shared.profileNotifications.long.inactiveProfileDuplicate")
    };
  }
  return {
    title: "",
    text: ""
  };
};
const getPassportScoreHint = ({
  score,
  t
}) => {
  if (!score) return "";
  switch (score.scoreCode) {
    case SizePassportScoreCode.SAFE:
      return t("shared.tooltip.scoreTextSafe");
    case SizePassportScoreCode.WARNING:
      return t("shared.tooltip.scoreTextWarn");
    case SizePassportScoreCode.RISK:
      return t("shared.tooltip.scoreTextRisk");
    default:
      return "";
  }
};
const getDisplayedFit = (profile, t) => {
  const {
    fitDescription,
    fitName
  } = profile.sizePassport;
  if (!fitName) {
    return void 0;
  }
  const translatedName = translatePassportTerm({
    prefix: "fits",
    t,
    term: fitName
  });
  if (!fitDescription) {
    return translatedName;
  }
  return `${translatedName} - ${translatePassportTerm({
    prefix: "fits",
    t,
    term: fitDescription
  })}`;
};
const mapMeasurementsToAdjustments = (measurements) => {
  const adjustments = [...measurements].filter((measurementItem) => Math.abs(measurementItem.alterationValue) > 0);
  if (adjustments.length === 0) {
    return [];
  }
  return [...adjustments].sort((a, b) => a.sequenceNo - b.sequenceNo);
};
const mergeBaseMeasurementValuesToAdjustedTarget = (originalMeasurements, adjustedMeasurementsSource) => {
  return originalMeasurements.map((originalMeasurement) => {
    const adjustedMeasurement = adjustedMeasurementsSource.find((adjustedMeasurement2) => adjustedMeasurement2.measurementId === originalMeasurement.measurementId);
    if (!adjustedMeasurement) return originalMeasurement;
    const newDefaultValue = adjustedMeasurement.value;
    const newAlterationValue = originalMeasurement.value - newDefaultValue;
    return __spreadProps(__spreadValues({}, originalMeasurement), {
      defaultValue: newDefaultValue,
      alterationValue: newAlterationValue
    });
  });
};
const getIncompatibleMeasurementsFromReport = (report) => {
  var _a;
  if (report.isCompatible) return [];
  if (!((_a = report.incompatibilityReasons) == null ? void 0 : _a.length)) {
    return [];
  }
  return report.incompatibilityReasons.filter((reason) => reason.compatibilityTypeCode === RtwInCompatibilityReasonCode.AlterationOutOfRange).map((reason) => reason.measurementId);
};
const getDisplayableAdjustmentsInfo = ({
  enforcePrefixPresence = false,
  defaultValue,
  value
}) => {
  const valuePrefix = value > 0 ? "+" : "";
  const prefixPresenceCondition = enforcePrefixPresence ? true : defaultValue !== 0;
  const alterationValue = prefixPresenceCondition ? `${valuePrefix}${value}` : `${value}`;
  return {
    alterationValue,
    isAdjustmentIncreasing: alterationValue.includes("+"),
    isAdjustmentDecreasing: alterationValue.includes("-")
  };
};
const getDisplayableAdjustmentsInfoFromMeasurement = (measurement, enforcePrefixPresence = false) => {
  return getDisplayableAdjustmentsInfo({
    value: measurement.alterationValue,
    enforcePrefixPresence,
    defaultValue: measurement.defaultValue
  });
};
const getHiddenMeasurementsByTagsPerClient = (client, productType) => {
  if (isRTWOfflineClient(client)) {
    if (productType.toLowerCase() === "shirt" || productType.toLowerCase() === "casualshirt") {
      return ["HemFormal"];
    }
    if (productType.toLowerCase() === "outerwearformal") {
      return ["LengthStandard"];
    }
  }
  return [];
};
const getHiddenMeasurementsByTag = (definitions, measurementTags) => {
  if (!measurementTags.length) return [];
  const measurementsWithTags = definitions.filter((definition) => {
    var _a;
    return (_a = definition.tagList) == null ? void 0 : _a.length;
  });
  if (!measurementsWithTags.length) return [];
  return measurementsWithTags.filter((definition) => !definition.tagList.find((tag) => measurementTags.includes(tag))).map((definition) => definition.id);
};
const getHiddenMeasurementsByMissingDefinitionDetails = (definitions, validationProp = "details") => {
  return definitions.filter((definition) => !definition[validationProp]).map((definition) => definition.id);
};
const getNotAlterableMeasurements = (definitions) => {
  return definitions.filter((definition) => !definition.alterationDetails).map((definition) => definition.id);
};
const getHiddenMeasurementsByDefinitions = (definitions, profileMeasurements) => {
  return definitions.filter((definition) => {
    if (!definition.alterationDetails) return true;
    const profileMeasurement = profileMeasurements.find((measurement) => measurement.measurementId === definition.id);
    if (!profileMeasurement) return true;
    const {
      alterationDetails,
      ignoreAlterationValues
    } = definition;
    const {
      maximumChange,
      tolerance
    } = alterationDetails;
    if (ignoreAlterationValues == null ? void 0 : ignoreAlterationValues.includes(profileMeasurement.alterationValue)) {
      return true;
    }
    if (definition.type.toLowerCase() !== "range" || definition.type.toLowerCase() === "range" && !definition.alterationDetails.tolerance) {
      return false;
    }
    const tolerationMaxValue = tolerance ? maximumChange + tolerance : maximumChange;
    return profileMeasurement.alterationValue > maximumChange && profileMeasurement.alterationValue <= tolerationMaxValue;
  }).map((definition) => definition.id);
};
export {
  getHiddenMeasurementsByTag as a,
  getHiddenMeasurementsByTagsPerClient as b,
  getHiddenMeasurementsByDefinitions as c,
  getDisplayableAdjustmentsInfoFromMeasurement as d,
  getPassportScoreHint as e,
  getDisplayedFit as f,
  getHiddenMeasurementsByMissingDefinitionDetails as g,
  useDataDogMonitoringInitializer as h,
  useDayJsUTCPlugin as i,
  getDisabledProfileContentCM as j,
  getIncompatibleMeasurementsFromReport as k,
  mergeBaseMeasurementValuesToAdjustedTarget as l,
  mapMeasurementsToAdjustments as m,
  getDisplayableAdjustmentsInfo as n,
  getIncompatibleNotificationTitle as o,
  getIncompatibleNotificationText as p,
  getNotAlterableMeasurements as q,
  useTimeLocale as u
};
